import type {
  AppThreadView,
  AppWorkSummary,
  ThreadTopicSummary,
  ThreadVisibleState,
} from '@shared/types/thread-work';
import type { WorkActionPresentation } from '@shared/types/work-action';
import {
  getStableTimestamp,
  getThreadTopicHomeZone,
  THREAD_VISIBLE_STATE_LABELS,
  type ActivityLivingWorkProjection,
  type ThreadHomeZone,
} from './thread-living-model';

/** The four steps of a piece of work, in the order a script goes through them. */
export type TodayStage = 'review' | 'todo' | 'running' | 'waiting';

export const TODAY_STAGES: ReadonlyArray<{
  stage: TodayStage;
  label: string;
  openByDefault: boolean;
}> = [
  { stage: 'review', label: 'À valider', openByDefault: true },
  { stage: 'todo', label: 'À faire', openByDefault: true },
  { stage: 'running', label: 'Domino travaille', openByDefault: false },
  { stage: 'waiting', label: 'En attente', openByDefault: false },
];

/** `blocked` means no executor covers the work: it stays the user's to do.
 * `needs_preparation` is open work without a proposal: the user prepares it.
 * `monitoring` is an exactly covered wait that Domino follows. */
export const getTodayStage = (state: ThreadVisibleState): TodayStage | null => {
  switch (state) {
    case 'awaiting_review':
      return 'review';
    case 'actionable':
    case 'needs_preparation':
    case 'blocked':
      return 'todo';
    case 'running':
      return 'running';
    case 'waiting':
    case 'monitoring':
      return 'waiting';
    default:
      return null;
  }
};

type ActiveZone = Exclude<ThreadHomeZone, 'quiet'>;

interface TodayRowBase {
  key: string;
  stage: TodayStage;
  zone: ActiveZone;
  topic: ThreadTopicSummary;
  ownerLabel: string;
}

export type TodayRow =
  | (TodayRowBase & { kind: 'account'; item: ActivityLivingWorkProjection })
  | (TodayRowBase & {
      kind: 'app';
      app: Pick<AppWorkSummary, 'flowId' | 'flowTitle' | 'appKey' | 'recordLaunches'>;
      thread: AppThreadView;
    });

const ZONE_RANK: Record<ActiveZone, number> = { now: 0, following: 1, verify: 2 };

const sourceKey = (topic: Pick<ThreadTopicSummary, 'sourceRunId' | 'sourceActionId'>) =>
  topic.sourceRunId && topic.sourceActionId
    ? `${topic.sourceRunId}\u0000${topic.sourceActionId}`
    : null;

const compareRows = (left: TodayRow, right: TodayRow): number =>
  ZONE_RANK[left.zone] - ZONE_RANK[right.zone]
  || (left.topic.priorityRank ?? Number.MAX_SAFE_INTEGER)
    - (right.topic.priorityRank ?? Number.MAX_SAFE_INTEGER)
  || getStableTimestamp(left.topic.dueAt) - getStableTimestamp(right.topic.dueAt)
  || left.ownerLabel.localeCompare(right.ownerLabel, 'fr')
  || left.topic.id.localeCompare(right.topic.id);

/**
 * One list for the day: account work and the work of apps outside any
 * account. A persistent Thread topic is the authority for its source action,
 * so a legacy projection of the same run action (exact run + action ids) is
 * dropped instead of being shown a second time, possibly in a stale state.
 */
export const buildTodayRows = (
  accountWorks: ActivityLivingWorkProjection[],
  apps: AppWorkSummary[],
): TodayRow[] => {
  const persistentKeys = new Set<string>();
  apps.forEach(({ thread }) => thread.topics.forEach((topic) => {
    const key = sourceKey(topic);
    if (key) persistentKeys.add(key);
  }));
  accountWorks.forEach((item) => {
    const key = item.subject.threadState?.source === 'persistent' ? sourceKey(item.topic) : null;
    if (key) persistentKeys.add(key);
  });

  const accountRows = accountWorks.flatMap((item): TodayRow[] => {
    const stage = getTodayStage(item.topic.visibleState);
    if (!stage) return [];
    const key = sourceKey(item.topic);
    if (item.subject.threadState?.source !== 'persistent' && key && persistentKeys.has(key)) return [];
    return [{
      kind: 'account', key: `account:${item.subject.subjectId}:${item.topic.sourceRunId ?? ''}:${item.topic.id}`, stage, zone: item.zone,
      topic: item.topic, ownerLabel: item.subject.subjectLabel, item,
    }];
  });
  const appRows = apps.flatMap(({ flowId, flowTitle, appKey, recordLaunches, thread }) => {
    const open = thread.currentTopic
      ? [thread.currentTopic, ...thread.otherTopics.filter((topic) => topic.id !== thread.currentTopic?.id)]
      : [];
    return open.flatMap((topic): TodayRow[] => {
      const stage = getTodayStage(topic.visibleState);
      const zone = getThreadTopicHomeZone(thread, topic);
      if (!stage || zone === 'quiet') return [];
      return [{
        kind: 'app', key: `app:${flowId}:${topic.id}`, stage, zone, topic,
        ownerLabel: flowTitle, app: { flowId, flowTitle, appKey, recordLaunches }, thread,
      }];
    });
  });
  return [...accountRows, ...appRows].sort(compareRows);
};

const rowHaystack = (row: TodayRow): string => [
  row.ownerLabel,
  row.topic.title,
  row.kind === 'account' ? row.item.work.title : null,
  row.kind === 'account' ? row.item.work.reason : row.topic.proposal?.rationale ?? row.topic.decision?.reason,
  row.kind === 'account' ? row.item.subject.subjectSubtitle : null,
  THREAD_VISIBLE_STATE_LABELS[row.topic.visibleState],
].filter((value): value is string => Boolean(value)).join(' ').toLocaleLowerCase('fr');

export const searchTodayRows = (rows: TodayRow[], search: string): TodayRow[] => {
  const query = search.trim().toLocaleLowerCase('fr');
  return query ? rows.filter((row) => rowHaystack(row).includes(query)) : rows;
};

/**
 * Short verb for the dense list, derived from the command kind only (never
 * from a label): `Lancer` runs a ready proposal, `Préparer` asks Domino for
 * one, `Relancer` retries a failed preparation, `Relire` opens work awaiting
 * validation. Null keeps the command's own label.
 */
export const getWorkVerb = (work: WorkActionPresentation): string | null => {
  const { command } = work;
  switch (command.kind) {
    case 'resume':
      return work.state === 'awaiting_review' ? 'Relire' : 'Voir';
    case 'task_proposal':
      return work.state === 'running' ? 'Voir' : 'Lancer';
    case 'tool_proposal':
      return 'Lancer';
    case 'forge':
      return 'Concevoir';
    case 'prepare_plan':
    case 'refresh':
      return command.retry ? 'Relancer' : 'Préparer';
    case 'work':
      return 'Préparer';
    default:
      return null;
  }
};
